import {
  Component,
  Input,
  ChangeDetectionStrategy,
  ViewEncapsulation,
} from '@angular/core';
import { NgClass } from '@angular/common';

export type SortButtonVariant = 'primary' | 'secondary' | 'ghost';
export type SortButtonSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'sortaify-icon-button',
  standalone: true,
  imports: [NgClass],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      [type]="type"
      [disabled]="disabled || loading"
      [ngClass]="buttonClasses"
      [attr.aria-label]="ariaLabel">
      @if (loading) {
        <span class="sortaify-btn__spinner" aria-label="Loading"></span>
      } @else {
        <ng-content></ng-content>
      }
    </button>
  `,
  styles: [`
    .sortaify-btn {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: none;
      cursor: pointer;
      font-family: var(--sortaify-font, inherit);
      font-weight: 600;
      overflow: hidden;
      background: transparent;
      transition: transform 150ms ease, box-shadow 150ms ease, color 150ms ease, border-radius 600ms cubic-bezier(0.23, 1, 0.32, 1);
    }
    .sortaify-btn:disabled { opacity: 0.5; cursor: not-allowed; pointer-events: none; }
    .sortaify-btn:focus-visible { outline: 2px solid var(--sortaify-accent-from, #3b82f6); outline-offset: 2px; }

    .sortaify-btn::before {
      content: '';
      position: absolute;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%) scale(0);
      width: 100%; height: 100%;
      border-radius: 100px;
      background: var(--sortaify-accent-gradient, linear-gradient(135deg, #3b82f6, #38bdf8, #0ea5e9));
      opacity: 0;
      transition: transform 600ms cubic-bezier(0.23, 1, 0.32, 1), opacity 600ms cubic-bezier(0.23, 1, 0.32, 1);
      pointer-events: none;
      z-index: 0;
    }
    .sortaify-btn:hover::before { transform: translate(-50%, -50%) scale(1); opacity: 0.18; }

    .sortaify-btn-icon { padding: 12px; border-radius: 100px; width: 44px; height: 44px; flex-shrink: 0; }
    .sortaify-btn-icon.sortaify-btn--sm { padding: 8px; width: 32px; height: 32px; }
    .sortaify-btn-icon.sortaify-btn--lg { padding: 16px; width: 56px; height: 56px; }
    .sortaify-btn-icon .mat-icon { position: relative; z-index: 1; font-size: 20px; width: 20px; height: 20px; line-height: 20px; }
    .sortaify-btn-icon.sortaify-btn--sm .mat-icon { font-size: 16px; width: 16px; height: 16px; line-height: 16px; }
    .sortaify-btn-icon.sortaify-btn--lg .mat-icon { font-size: 24px; width: 24px; height: 24px; line-height: 24px; }

    .sortaify-btn-primary {
      background: var(--sortaify-accent-gradient, linear-gradient(135deg, #3b82f6, #38bdf8, #0ea5e9)) !important;
      color: #ffffff !important;
      box-shadow: var(--sortaify-shadow-glow, 0 0 32px rgba(59,130,246,0.2));
    }
    .sortaify-btn-primary:hover:not(:disabled) { box-shadow: 0 0 48px rgba(59,130,246,0.45); border-radius: 12px !important; }

    .sortaify-btn-secondary {
      background: var(--sortaify-bg-card-elevated, rgba(20,20,20,0.9)) !important;
      color: var(--sortaify-text-primary, #ffffff) !important;
      border: 1px solid var(--sortaify-border, rgba(255,255,255,0.1)) !important;
      backdrop-filter: blur(12px);
    }
    .sortaify-btn-secondary::before { background: var(--sortaify-accent-gradient); }
    .sortaify-btn-secondary:hover:not(:disabled) { border-color: rgba(59,130,246,0.35) !important; }

    .sortaify-btn-ghost { background: transparent; color: var(--sortaify-text-secondary, rgba(255,255,255,0.7)); }
    .sortaify-btn-ghost::before { display: none; }
    .sortaify-btn-ghost:hover:not(:disabled) { color: var(--sortaify-text-primary, #ffffff); background: var(--sortaify-bg-hover, rgba(30,30,30,0.8)); }

    .sortaify-btn:active { transform: scale(0.95); box-shadow: 0 0 0 4px var(--sortaify-accent-from, #3b82f6); }

    .sortaify-btn__spinner {
      width: 16px; height: 16px;
      border: 2px solid rgba(255,255,255,0.3);
      border-top-color: #ffffff;
      border-radius: 50%;
      animation: sortaify-spin 0.6s linear infinite;
      position: relative; z-index: 1;
      flex-shrink: 0;
    }
    @keyframes sortaify-spin { to { transform: rotate(360deg); } }
  `]
})
export class SortIconButtonComponent {
  @Input() variant: SortButtonVariant = 'primary';
  @Input() size: SortButtonSize = 'md';
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() disabled = false;
  @Input() loading = false;
  @Input() ariaLabel: string = '';

  get buttonClasses(): string {
    return ['sortaify-btn', 'sortaify-btn-icon', `sortaify-btn-${this.variant}`, `sortaify-btn--${this.size}`].join(' ');
  }
}
